// src/utils/StorageTracker.js

import { db } from "../firebase";
import { doc, getDoc, setDoc, increment, updateDoc } from "firebase/firestore";
import { usePlan } from "../context/PlanContext";
import { useState, useEffect } from "react";

// Plan storage limits in bytes
export const PLAN_LIMITS = {
  standard: {
    maxStorage: 1 * 1024 * 1024 * 1024, // 1GB
    maxAccounts: 1,
  },
  pro: {
    maxStorage: 5 * 1024 * 1024 * 1024, // 5GB
    maxAccounts: 20,
  },
};

/**
 * Calculate the size of an object in bytes
 * @param {Object} object - The object to calculate size for
 * @returns {number} Size in bytes
 */
export const calculateObjectSize = (object) => {
  // Convert to JSON string to get approximate size
  const jsonString = JSON.stringify(object);
  // Each character in a string is typically 2 bytes in JavaScript
  return jsonString.length * 2;
};

/**
 * Update storage usage for a user
 * @param {string} userId - The user's ID
 * @param {number} bytes - Size in bytes to add
 * @returns {Promise<void>}
 */
export const updateStorageUsage = async (userId, bytes) => {
  if (!userId) return;

  try {
    const storageRef = doc(db, "storageUsage", userId);

    // First check if document exists
    const storageDoc = await getDoc(storageRef);

    if (storageDoc.exists()) {
      // Update existing document
      await updateDoc(storageRef, {
        bytesUsed: increment(bytes),
        lastUpdated: new Date(),
      });
    } else {
      // Create new document
      await setDoc(storageRef, {
        userId,
        bytesUsed: bytes,
        createdAt: new Date(),
        lastUpdated: new Date(),
      });
    }

    // Dispatch event to update UI
    window.dispatchEvent(new CustomEvent("storageUpdated"));
  } catch (error) {
    console.error("Error updating storage usage:", error);
  }
};

/**
 * Get current storage usage for a user
 * @param {string} userId - The user's ID
 * @returns {Promise<number>} Size in bytes used
 */
export const getStorageUsage = async (userId) => {
  if (!userId) return 0;

  try {
    const storageRef = doc(db, "storageUsage", userId);
    const storageDoc = await getDoc(storageRef);

    if (storageDoc.exists()) {
      return storageDoc.data().bytesUsed || 0;
    }
    return 0;
  } catch (error) {
    console.error("Error getting storage usage:", error);
    return 0;
  }
};

/**
 * Check if user has exceeded storage limit
 * @param {string} userId - The user's ID
 * @returns {Promise<Object>} Object with limit information
 */
export const checkStorageLimit = async (userId) => {
  if (!userId)
    return { hasExceededLimit: false, currentStorage: 0, maxStorage: 0 };

  try {
    const usage = await getStorageUsage(userId);

    // Get user's plan
    const planRef = doc(db, "userPlans", userId);
    const planDoc = await getDoc(planRef);

    let planType = "standard";
    if (planDoc.exists()) {
      planType = planDoc.data().planType || "standard";
    }

    const limit = PLAN_LIMITS[planType].maxStorage;
    const hasExceededLimit = usage >= limit;

    return {
      hasExceededLimit,
      currentStorage: usage,
      maxStorage: limit,
      remainingStorage: Math.max(0, limit - usage),
      percentUsed: limit > 0 ? (usage / limit) * 100 : 100,
    };
  } catch (error) {
    console.error("Error checking storage limit:", error);
    return {
      hasExceededLimit: false,
      currentStorage: 0,
      maxStorage: PLAN_LIMITS.standard.maxStorage,
    };
  }
};

/**
 * Format bytes to human-readable format
 * @param {number} bytes - Size in bytes
 * @returns {string} Formatted string (e.g., "1.5 MB")
 */
export const formatBytes = (bytes) => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

/**
 * Track storage for a new document
 * @param {string} userId - The user's ID
 * @param {Object} data - The document data
 * @param {string} operation - 'add', 'update', or 'delete'
 * @returns {Promise<{success: boolean, error: string|null}>} Success status and optional error
 */
export const trackDocumentStorage = async (userId, data, operation = "add") => {
  if (!userId) return { success: false, error: "No user ID provided" };

  try {
    // Calculate size
    const size = calculateObjectSize(data);

    // For add operations, check if user has exceeded storage limit
    if (operation === "add") {
      const { hasExceededLimit, currentStorage, maxStorage } =
        await checkStorageLimit(userId);

      if (hasExceededLimit) {
        return {
          success: false,
          error: `Storage limit exceeded. You are using ${formatBytes(
            currentStorage
          )} of your ${formatBytes(maxStorage)} limit.`,
        };
      }

      // Check if this operation would put the user over the limit
      if (currentStorage + size > maxStorage) {
        return {
          success: false,
          error: `This operation would exceed your storage limit of ${formatBytes(
            maxStorage
          )}.`,
        };
      }
    }

    // Update storage usage
    if (operation === "add") {
      await updateStorageUsage(userId, size);
    } else if (operation === "update") {
      // For updates, calculate the difference (simplified - assuming size increases)
      await updateStorageUsage(userId, size / 10); // Rough estimate of change
    } else if (operation === "delete") {
      await updateStorageUsage(userId, -size);
    }

    return { success: true, error: null };
  } catch (error) {
    console.error("Error tracking document storage:", error);
    return { success: false, error: error.message };
  }
};

/**
 * React hook for storage usage
 */
export const useStorageUsage = (userId) => {
  const [usage, setUsage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [percentUsed, setPercentUsed] = useState(0);
  const [maxStorage, setMaxStorage] = useState(0);
  const { planData } = usePlan();

  useEffect(() => {
    const fetchUsage = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const limitInfo = await checkStorageLimit(userId);
        setUsage(limitInfo.currentStorage);
        setIsOverLimit(limitInfo.hasExceededLimit);
        setPercentUsed(limitInfo.percentUsed);
        setMaxStorage(limitInfo.maxStorage);
      } catch (error) {
        console.error("Error in useStorageUsage:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsage();

    const handleStorageUpdate = () => {
      fetchUsage();
    };

    window.addEventListener("storageUpdated", handleStorageUpdate);
    return () => {
      window.removeEventListener("storageUpdated", handleStorageUpdate);
    };
  }, [userId, planData]);

  return {
    usage,
    isLoading,
    isOverLimit,
    percentUsed,
    maxStorage,
    formattedUsage: formatBytes(usage),
    formattedMaxStorage: formatBytes(maxStorage),
  };
};
